import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CreateTradingAreaRequest,
    TradingArea,
    TradingAreaResponse,
    TradingAreasRequest,
    UpdateTradingAreaRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/trading-areas.api.ts';
import {
    createApiTradingArea,
    getApiTradingAreas,
    updateApiTradingArea,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/trading-areas.api.ts';
import { extractError } from '@utils/slice.ts';

export interface TradingAreasError {
    message?: string;
    [k: string]: any;
}

export interface TradingAreasState {
    error: TradingAreasError | null;

    commonTradingAreas: TradingAreaResponse | null; // use for common data just in case
    tradingAreas: TradingArea[];
    pagination: CommonTablePaginatedResponse;
    updatedTradingArea: TradingArea | null;
    currentGetRequestId: string | null;

    isGetTradingAreasPending: boolean;
    isCreateTradingAreaPending: boolean;
    isUpdateTradingAreaPending: boolean;
}

const initialState: TradingAreasState = {
    error: null,

    commonTradingAreas: null,
    tradingAreas: [],
    updatedTradingArea: null,
    currentGetRequestId: null,

    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },

    isGetTradingAreasPending: false,
    isCreateTradingAreaPending: false,
    isUpdateTradingAreaPending: false,
};

export const getTradingAreas = createAsyncThunk<
    TradingAreaResponse,
    { clientId: number; tenantAlias: string; payload: TradingAreasRequest },
    { rejectValue: TradingAreasError }
>('adminCenterTradingAreas/getTradingAreas', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiTradingAreas(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createTradingArea = createAsyncThunk<
    TradingArea,
    { clientId: number; tenantAlias: string; payload: CreateTradingAreaRequest },
    { rejectValue: TradingAreasError }
>('adminCenterTradingAreas/createTradingArea', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    createApiTradingArea(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateTradingArea = createAsyncThunk<
    TradingArea,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateTradingAreaRequest },
    { rejectValue: TradingAreasError }
>('adminCenterTradingAreas/updateTradingArea', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiTradingArea(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const tradingAreasSlice = createSlice({
    name: 'adminCenterTradingAreas',
    initialState,
    reducers: {
        resetTradingAreasState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(getTradingAreas.pending, (state, action) => {
            state.isGetTradingAreasPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getTradingAreas.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetTradingAreasPending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonTradingAreas = action.payload;
            state.tradingAreas = data ?? [];
            state.pagination = pagination;
        });
        builder.addCase(getTradingAreas.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetTradingAreasPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(createTradingArea.pending, (state) => {
            state.isCreateTradingAreaPending = true;
            state.error = null;
        });
        builder.addCase(createTradingArea.fulfilled, (state, action) => {
            state.isCreateTradingAreaPending = false;
            state.updatedTradingArea = action.payload;
        });
        builder.addCase(createTradingArea.rejected, (state, action) => {
            state.isCreateTradingAreaPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateTradingArea.pending, (state) => {
            state.isUpdateTradingAreaPending = true;
            state.error = null;
        });
        builder.addCase(updateTradingArea.fulfilled, (state, action) => {
            state.isUpdateTradingAreaPending = false;
            state.updatedTradingArea = action.payload;
        });
        builder.addCase(updateTradingArea.rejected, (state, action) => {
            state.isUpdateTradingAreaPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const { tradingAreaResponse } = action.payload;

            if (!tradingAreaResponse) return;

            const { data, pagination } = tradingAreaResponse;
            state.commonTradingAreas = tradingAreaResponse;
            state.tradingAreas = data ?? [];
            state.pagination = pagination;
        });
    },
});

export const { resetTradingAreasState } = tradingAreasSlice.actions;
export default tradingAreasSlice.reducer;
